import React, { useState } from "react";
import { cn } from "@/lib/utils";

interface LogoAvatarProps {
  src?: string | null;
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  shape?: "square" | "rounded" | "full";
}

const GRADIENTS = [
  "from-rose-500 to-pink-600 text-white",
  "from-indigo-500 to-purple-600 text-white",
  "from-emerald-500 to-teal-600 text-white",
  "from-amber-500 to-orange-600 text-white",
  "from-sky-500 to-blue-600 text-white",
  "from-violet-500 to-fuchsia-600 text-white",
];

const SIZE_CLASSES = {
  xs: "h-6 w-6 text-xs font-semibold",
  sm: "h-8 w-8 text-sm font-semibold",
  md: "h-10 w-10 text-base font-bold",
  lg: "h-12 w-12 text-lg font-bold",
  xl: "h-16 w-16 text-2xl font-bold",
};

const SHAPE_CLASSES = {
  square: "rounded-md",
  rounded: "rounded-xl",
  full: "rounded-full",
};

export const LogoAvatar: React.FC<LogoAvatarProps> = ({
  src,
  name,
  size = "md",
  className,
  shape = "rounded",
}) => {
  const [imageError, setImageError] = useState(false);

  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";

  // Deterministic gradient selection based on character codes
  const charCode = (name || "?")
    .split("")
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const gradient = GRADIENTS[charCode % GRADIENTS.length];

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const shapeClass = SHAPE_CLASSES[shape] || SHAPE_CLASSES.rounded;

  if (src && !imageError) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setImageError(true)}
        className={cn(
          "shrink-0 object-cover border border-border/80 shadow-xs transition-opacity duration-200",
          sizeClass,
          shapeClass,
          className,
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "shrink-0 flex items-center justify-center bg-gradient-to-br shadow-xs border border-white/20 select-none relative overflow-hidden",
        gradient,
        sizeClass,
        shapeClass,
        className,
      )}
      title={name}
    >
      <span className="relative z-10 drop-shadow-sm tracking-wide">
        {initial}
      </span>
      <div className="absolute inset-0 bg-white/10 opacity-50 pointer-events-none" />
    </div>
  );
};
